import { Fragment, useCallback, useMemo, useState } from 'react';
import Loading from '../../components/common/Loading.component';
import { useSelector } from 'react-redux';
import Alert from '../../components/common/Alert.component';
import { fetchAPI } from '../../api/request';
import { useNavigate } from 'react-router-dom';
import DuplicateButton from '../../components/duplicate/DuplicateButton.component';
import _ from 'lodash';

const DuplicateTemplate = ({
  module: { tag, query, detail },
  data,
  isIcon = false,
  disabled = false,
}) => {
  const navigate = useNavigate();
  const { jwt } = useSelector((state) => state.login);
  const userAuth = useSelector((state) => state.login?.user);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const hasPermissions = useMemo(
    () => userAuth.permissions[_.toUpper(tag)]?.CREATE === 'true',
    [tag, userAuth]
  );
  const message = useMemo(
    () => `Duplicate operation from ID: ${data?.id}`,
    [data]
  );
  const onError = useCallback(() => {
    setSuccess(false);
    setLoading(false);
    setError(true);
    // eslint-disable-next-line
  }, []);

  const onSuccess = useCallback(
    (resp) => {
      const id = resp?.data?.id;
      setTimeout(
        () =>
          navigate(`${detail?.duplicate?.navigate}/${id}/edit`, {
            state: { from: 'duplicate', message: message || null },
          }),
        1000
      );
      setSuccess(true);
      setLoading(false);
      setError(false);
    },
    [setLoading, setSuccess, detail, navigate, message]
  );

  const onSubmitReservation = useCallback(
    (resp) => {
      const id = resp?.data?.id;

      fetchAPI(
        query.duplicate.settings2,
        {},
        '',
        { id_operation_list: id, ...data },
        jwt
      )
        .then(() => onSuccess(resp))
        .catch(onError);
    },
    [onSuccess, data, jwt, query, onError]
  );

  const onSubmit = useCallback(() => {
    setError(false);
    setSuccess(false);
    setLoading(true);
    if (hasPermissions)
      fetchAPI(query.duplicate.settings, {}, '', { ...data }, jwt)
        .then((resp) =>
          query?.duplicate?.settings2
            ? onSubmitReservation(resp)
            : onSuccess(resp)
        )
        .catch(onError);
  }, [
    setLoading,
    jwt,
    onError,
    onSuccess,
    query,
    data,
    onSubmitReservation,
    hasPermissions,
  ]);

  return (
    hasPermissions && (
      <Fragment>
        {loading && <Loading />}
        {success && <Alert severity='success'>{'Success: Duplicate'}</Alert>}
        {error && <Alert severity='error'>{'An error has occurred'}</Alert>}
        <DuplicateButton
          isIcon={isIcon}
          onClick={onSubmit}
          disabled={disabled || success}
        />
      </Fragment>
    )
  );
};

export default DuplicateTemplate;
